import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import type { LabeledBarChartViz } from 'shared/src/api/ai-labeled-viz-types';
import type { ValueFormat } from 'shared/src/api/ai-viz-types';
import { transformBarChart, formatVizValue } from 'shared/src/utils/ai/ai-viz-transformers';
import type { CubeRow } from './chart-utils';
import { CHART_PALETTE, TOOLTIP_STYLE } from './chart-utils';

type BarChartProps = {
  rows: CubeRow[];
  viz: LabeledBarChartViz;
};

export function BarChart({ rows, viz }: BarChartProps) {
  const data = transformBarChart(rows, viz);
  const { valueKeys } = viz.mapping;
  const { categoryLabel, valueLabels, axisFormat } = viz.labels;
  const isHorizontal = viz.orientation === 'horizontal';

  const hasData = data.some(row => valueKeys.some(k => row[k] !== null && row[k] !== undefined));

  if (!hasData) {
    return (
      <div className="w-full bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">{viz.title}</h2>
        <div className="flex items-center justify-center" style={{ height: 450 }}>
          <p className="text-gray-400 text-lg">No data available</p>
        </div>
      </div>
    );
  }

  const barRadius: [number, number, number, number] = isHorizontal ? [0, 8, 8, 0] : [8, 8, 0, 0];

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg p-6">
      <h2 className="text-2xl font-semibold mb-4">{viz.title}</h2>

      <ResponsiveContainer width="100%" height={450}>
        <RechartsBarChart
          data={data}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 8, right: 24, left: 8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />

          {isHorizontal ? (
            <>
              <YAxis dataKey="category" type="category" tick={{ fontSize: 12 }} width={120} />
              <XAxis
                type="number"
                tickFormatter={(v: number) => formatVizValue(v, axisFormat)}
                tick={{ fontSize: 12 }}
              />
            </>
          ) : (
            <>
              <XAxis dataKey="category" type="category" tick={{ fontSize: 12 }} />
              <YAxis
                tickFormatter={(v: number) => formatVizValue(v, axisFormat)}
                tick={{ fontSize: 12 }}
              />
            </>
          )}

          <Tooltip
            content={
              <CustomTooltip
                categoryLabel={categoryLabel}
                valueLabels={valueLabels}
                axisFormat={axisFormat}
                stacked={viz.stacked}
                valueKeys={valueKeys}
              />
            }
            isAnimationActive={false}
            cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          />

          <Legend wrapperStyle={{ paddingTop: 6, fontSize: 12 }} iconSize={10} />

          {valueKeys.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              name={valueLabels[key] ?? key}
              fill={CHART_PALETTE[i % CHART_PALETTE.length]}
              stackId={viz.stacked ? 'a' : undefined}
              radius={barRadius}
              isAnimationActive={false}
            />
          ))}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Internal helpers (in call order) ───

function CustomTooltip({
  active,
  payload,
  categoryLabel,
  valueLabels,
  axisFormat,
  stacked,
  valueKeys
}: {
  active?: boolean;
  payload?: Array<{
    dataKey: string;
    value: number;
    color: string;
    payload: Record<string, string | number | null>;
  }>;
  categoryLabel: string;
  valueLabels: Record<string, string>;
  axisFormat: ValueFormat;
  stacked: boolean;
  valueKeys: string[];
}) {
  if (!active || !payload?.length) return null;

  const row = payload[0].payload;
  const categoryValue = row.category;

  const stackTotal = stacked
    ? valueKeys.reduce((sum, k) => {
        const v = row[k];
        return sum + (typeof v === 'number' ? v : 0);
      }, 0)
    : 0;

  return (
    <div style={TOOLTIP_STYLE}>
      <p style={{ marginBottom: 6, fontWeight: 600 }}>
        {categoryLabel}: {categoryValue}
      </p>
      {payload.map(entry => {
        const pct =
          stacked && stackTotal > 0
            ? ` · ${((entry.value / stackTotal) * 100).toFixed(1)}% of total`
            : '';
        return (
          <p key={entry.dataKey} style={{ margin: '2px 0', color: entry.color }}>
            {valueLabels[entry.dataKey] ?? entry.dataKey}: {formatVizValue(entry.value, axisFormat)}
            {pct}
          </p>
        );
      })}
    </div>
  );
}
